// Ingest API keys (task 39, PLAN §14 M4): programs send files to Hoopoe with a key an admin issued. Each key has
// its own sensitivity floor and quotas; what it sends goes through the same IngestWorkflow as a manual upload.
import { type ExtractionDepth, formatBytes, type ReviewMode, type Sensitivity } from './uploads';

export type ApiKeySensitivity = Sensitivity;

/** Every key starts with this, so leaked keys are easy to recognise (and to search for in logs and repositories). */
export const API_KEY_PREFIX = 'hpk_';
/** Characters of the key shown in lists after it is created (`hpk_` + 8), enough to tell keys apart. */
export const API_KEY_VISIBLE = API_KEY_PREFIX.length + 8;

const MB = 1024 * 1024;

/**
 * Largest file one request may carry. Cloudflare refuses request bodies over 100 MB on this plan, so bigger files
 * go through the Upload page (resumable, up to 200 MB).
 */
export const API_MAX_FILE_BYTES = 100 * MB;

/** Quotas a new key gets unless the admin changes them. */
export const API_KEY_DEFAULTS = {
  dailyFiles: 500,
  dailyBytes: 2048 * MB,
  maxFileBytes: API_MAX_FILE_BYTES,
  /** Requests per minute, any endpoint (status checks included). */
  perMinute: 60,
} as const;

export const API_KEY_LIMITS = {
  dailyFiles: { min: 1, max: 100_000 },
  dailyBytes: { min: MB, max: 1024 * 1024 * MB },
  maxFileBytes: { min: 1024, max: API_MAX_FILE_BYTES },
} as const;

export interface ApiKeyQuotas {
  dailyFiles: number;
  dailyBytes: number;
  maxFileBytes: number;
}

export interface ApiKeySummary extends ApiKeyQuotas {
  id: string;
  name: string;
  /** The first characters of the key, e.g. `hpk_Ab3dE9xY`. The rest is never stored. */
  prefix: string;
  sensitivity: ApiKeySensitivity;
  reviewMode: ReviewMode;
  /** Null: proposed per file from its type, as on the Upload page. */
  extractionDepth: ExtractionDepth | null;
  /** The person the key acts for: its files are theirs (progress, notifications, "my uploads"). */
  owner: { id: string; name: string | null; email: string; active: boolean };
  status: 'active' | 'revoked' | 'expired';
  createdAt: number;
  expiresAt: number | null;
  revokedAt: number | null;
  lastUsedAt: number | null;
  /** Use today (UTC). */
  today: { files: number; bytes: number };
  /** Files sent with this key, all time. */
  totalFiles: number;
}

export interface ApiKeyInput {
  name?: string;
  sensitivity?: ApiKeySensitivity;
  reviewMode?: ReviewMode;
  extractionDepth?: ExtractionDepth | null;
  dailyFiles?: number;
  dailyBytes?: number;
  maxFileBytes?: number;
  /** ms since epoch; null for no expiry. */
  expiresAt?: number | null;
}

/** The only time the full key exists outside the caller's own storage. */
export interface CreatedApiKey {
  key: ApiKeySummary;
  secret: string;
}

/** UTC day (`YYYY-MM-DD`) that quotas count against. */
export const utcDay = (ms = Date.now()) => new Date(ms).toISOString().slice(0, 10);

/** Seconds until the daily quotas reset (next UTC midnight), for `Retry-After`. */
export function secondsUntilReset(ms = Date.now()): number {
  const d = new Date(ms);
  const next = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1);
  return Math.max(1, Math.ceil((next - ms) / 1000));
}

/**
 * The sensitivity a file sent with a key gets: the key's level is a floor. A standard key may mark a single file
 * confidential; a confidential key's files are always confidential, whatever the request says.
 */
export function effectiveSensitivity(key: ApiKeySensitivity, requested: unknown): ApiKeySensitivity {
  return key === 'confidential' || requested === 'confidential' ? 'confidential' : 'standard';
}

export type QuotaCheck = { ok: true } | { ok: false; reason: 'file_too_large' | 'daily_files' | 'daily_bytes' };

/** Whether one more file of `size` bytes fits the key's quotas, given today's use. */
export function checkQuota(q: ApiKeyQuotas, used: { files: number; bytes: number }, size: number): QuotaCheck {
  if (size > q.maxFileBytes) return { ok: false, reason: 'file_too_large' };
  if (used.files + 1 > q.dailyFiles) return { ok: false, reason: 'daily_files' };
  if (used.bytes + size > q.dailyBytes) return { ok: false, reason: 'daily_bytes' };
  return { ok: true };
}

/** Validates the admin's key settings; returns plain-language problems (empty when fine). */
export function validateApiKeyInput(input: ApiKeyInput, creating: boolean): string[] {
  const errors: string[] = [];
  if (creating || input.name !== undefined) {
    const name = (input.name ?? '').trim();
    if (!name) errors.push('Give the key a name, e.g. the program that will use it.');
    else if (name.length > 80) errors.push('Keep the name under 80 characters.');
  }
  const inRange = (v: number | undefined, r: { min: number; max: number }) =>
    v === undefined || (Number.isInteger(v) && v >= r.min && v <= r.max);
  if (!inRange(input.dailyFiles, API_KEY_LIMITS.dailyFiles))
    errors.push(
      `Files per day must be between ${API_KEY_LIMITS.dailyFiles.min} and ${API_KEY_LIMITS.dailyFiles.max.toLocaleString('en')}.`,
    );
  if (!inRange(input.dailyBytes, API_KEY_LIMITS.dailyBytes))
    errors.push(
      `Data per day must be between ${formatBytes(API_KEY_LIMITS.dailyBytes.min)} and ${formatBytes(API_KEY_LIMITS.dailyBytes.max)}.`,
    );
  if (!inRange(input.maxFileBytes, API_KEY_LIMITS.maxFileBytes))
    errors.push(`The largest file must be between 1 KB and ${formatBytes(API_KEY_LIMITS.maxFileBytes.max)}.`);
  if (input.expiresAt != null && (!Number.isFinite(input.expiresAt) || input.expiresAt <= Date.now()))
    errors.push('The expiry date must be in the future.');
  return errors;
}
